import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { socialAuthApi } from '../services/api';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle } from 'lucide-react';
import MfaChallengeStep from '../components/auth/MfaChallengeStep';
import BusyBeeLogo from '../components/BusyBeeLogo';
import { takeSignInReturn } from '../lib/signInReturn';

/**
 * Lands after backend `/oauth/{provider}/callback` 302s back to us with
 * `?handoff=...&provider=...&error=...`. We POST the handoff to backend,
 * receive either a JWT (happy path) or an MFA challenge.
 *
 * Same component is reused for the link variant (route `/auth/oauth/link-return`)
 * via the `linkMode` prop - in that case success means "identity linked"
 * (already authenticated), so we navigate back to Settings instead of /app.
 */
const OAuthReturnPage = ({ linkMode = false }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { finalizeSession } = useAuth();
  const [error, setError] = useState('');
  const [challenge, setChallenge] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const handoff = searchParams.get('handoff');
    const incomingError = searchParams.get('error');
    // The app's native sign-in (src/native/auth.js) lands here only for the second factor.
    const nativeChallenge = searchParams.get('challenge');
    if (nativeChallenge) {
      setChallenge(nativeChallenge);
      return;
    }

    if (incomingError) {
      setError(incomingError);
      return;
    }
    if (!handoff) {
      setError(t('auth.oauth.missingHandoff'));
      return;
    }

    (async () => {
      try {
        const { data } = await socialAuthApi.consumeHandoff(handoff);
        if (data.mfa_required) {
          setChallenge(data.challenge_token);
          return;
        }
        if (linkMode) {
          // Identity was linked to the already-authenticated user. Bounce
          // back to Settings, where the UI re-fetches /identities.
          navigate('/app/settings?tab=security&linked=1');
          return;
        }
        await finalizeSession(data.access_token);
        navigate(takeSignInReturn(), { replace: true });
      } catch (err) {
        setError(err.response?.data?.detail || t('auth.errors.generic'));
      }
    })();
  }, [searchParams, navigate, finalizeSession, linkMode, t]);

  const handleMfaSuccess = async (accessToken) => {
    await finalizeSession(accessToken);
    navigate(takeSignInReturn(), { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <BusyBeeLogo className="h-10" />
        </div>
        {challenge ? (
          <MfaChallengeStep
            challengeToken={challenge}
            onSuccess={handleMfaSuccess}
            onCancel={() => navigate('/login')}
          />
        ) : error ? (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <p className="text-red-400 text-sm font-medium">{error}</p>
              <button
                onClick={() => navigate(linkMode ? '/app/settings?tab=security' : '/login')}
                className="text-amber-400 text-sm hover:text-amber-300 mt-3 underline"
              >
                {linkMode ? t('auth.backToSettings') : t('auth.backToLogin')}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 justify-center text-gray-300">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t('auth.oauth.signingIn')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default OAuthReturnPage;
